import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { Field, reduxForm, reset } from 'redux-form';
import { createStructuredSelector } from 'reselect';
import { withStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';

import styles from './styles';
import Button from '../../../Button';
import {
  FORMS,
  VIEWABLE_FORM_MODES,
  USER_PROFILE_TYPES,
} from '../../../../constants';
import {
  selectAdditionalInfoFormMode,
  selectIsUpdatingAdditionalInfo,
} from '../../../../store/selectors';
import {
  editUserProfile,
  editUserProfileCancelled,
  updateUserProfile,
} from '../../../../store/actions/userProfile';
import TextField from '../../../../fields/reduxForm/ViewableTextField';
import { positiveNumber } from '../../../../utilities/validators';
import SelectFormField from '../../../../fields/reduxForm/SelectFormField';

const propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  classes: PropTypes.shape({
    actionsRow: PropTypes.string,
  }).isRequired,
  initialValues: PropTypes.object.isRequired,
  isUpdatingAdditionalInfo: PropTypes.bool.isRequired,
  additionalinfoFormMode: PropTypes.oneOf([
    VIEWABLE_FORM_MODES.EDITING,
    VIEWABLE_FORM_MODES.VIEWING,
  ]).isRequired,
  onEditUserProfile: PropTypes.func.isRequired,
  onEditUserProfileCancelled: PropTypes.func.isRequired,
  onResetForm: PropTypes.func.isRequired,
  onUpdateUserProfile: PropTypes.func.isRequired,
  enableEditing: PropTypes.bool.isRequired,
  userAttributes: PropTypes.object.isRequired,
  setBuilder: PropTypes.func.isRequired,
};

export const AdditionalInformationsForm = props => {
  const {
    classes,
    isUpdatingAdditionalInfo,
    additionalinfoFormMode,
    onEditUserProfile,
    onEditUserProfileCancelled,
    onResetForm,
    handleSubmit,
    initialValues,
    onUpdateUserProfile,
    enableEditing,
    userAttributes,
    setBuilder,
  } = props;
  // console.log(
  //   '🚀 ~ file: index.js ~ line 62 ~ userAttributes Form',
  //   userAttributes,
  // );
  const [previousValues, setPreviousValues] = React.useState({});

  // console.log('🚀 ~ file: index.js ~ line 61 ~ initialValues', initialValues);
  useEffect(() => {
    setPreviousValues({
      'custom:newBuildsPerYear': userAttributes['custom:newBuildsPerYear'],
      'custom:businessYears': userAttributes['custom:businessYears'],
      'custom:employeesCount': userAttributes['custom:employeesCount'],
      'custom:renovationsPerYear': userAttributes['custom:renovationsPerYear'],
    });
  }, [userAttributes]);

  useEffect(
    () => () => {
      onResetForm(FORMS.BUILDER_PROFILE_ADDITIONAL);
      onEditUserProfileCancelled(USER_PROFILE_TYPES.ADDITIONAL);
    },
    [],
  );

  const handleFormSubmitted = values => {
    // update email which is used for user explicitly
    const updatedValues = {
      ...values,
    };
    setBuilder(Object.assign({}, userAttributes, updatedValues));
    onUpdateUserProfile(
      localStorage.getItem('accessToken'),
      USER_PROFILE_TYPES.ADDITIONAL,
      updatedValues,
      null,
      userAttributes.username,
      previousValues,
    );
  };

  const renderFormButton = () => {
    if (additionalinfoFormMode === VIEWABLE_FORM_MODES.VIEWING) {
      return (
        <div className={classes.editButton}>
          <Button
            type="button"
            variant="outlined"
            color="primary"
            fullWidth
            onClick={handleClick}
            disabled={!enableEditing}
          >
            Edit
          </Button>
        </div>
      );
    }

    return (
      <div className={classes.formButtons}>
        <Button
          type="button"
          variant="outlined"
          color="primary"
          onClick={handleFormCancel}
          disabled={!enableEditing || isUpdatingAdditionalInfo}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          color="primary"
          onClick={handleSubmit(handleFormSubmitted)}
          disabled={!enableEditing || isUpdatingAdditionalInfo}
        >
          {isUpdatingAdditionalInfo && <CircularProgress size={20} />}
          {!isUpdatingAdditionalInfo && 'Done'}
        </Button>
      </div>
    );
  };

  const handleFormCancel = e => {
    e.preventDefault();
    onResetForm(FORMS.BUILDER_PROFILE_ADDITIONAL);
    onEditUserProfileCancelled(USER_PROFILE_TYPES.ADDITIONAL);
  };

  const handleClick = () => {
    onEditUserProfile(USER_PROFILE_TYPES.ADDITIONAL);
  };

  const getNumberOfNewBuildsOptions = () => {
    const options = ['0', '1-3', '4-10', '11-20', '20+'].map(data => (
      <option value={data}>{data}</option>
    ));
    return options;
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className={classes.formRow}>
        <div
          className={classNames(classes.formGroup, classes.halfFormGroup)}
          style={{ display: 'flex' }}
        >
          <Field
            name="custom:newBuildsPerYear"
            component={SelectFormField}
            props={{
              id: 'newBuildsPerYear-text-field',
              label: 'Number of New Builds completed over the past year?',
              fullWidth: true,
              enableEditing:
                additionalinfoFormMode !== VIEWABLE_FORM_MODES.EDITING,
            }}
            initialValue={initialValues['custom:newBuildsPerYear']}
            isEditing={additionalinfoFormMode === VIEWABLE_FORM_MODES.EDITING}
          >
            <option value="" />
            {getNumberOfNewBuildsOptions()}
          </Field>
        </div>
        <div
          className={classNames(classes.formGroup, classes.halfFormGroup)}
          style={{ display: 'flex' }}
        >
          <Field
            name="custom:renovationsPerYear"
            component={SelectFormField}
            props={{
              id: 'renovationsPerYear-text-field',
              label: 'Number of Renovations completed over the past year?.',
              fullWidth: true,
              required: true,
              enableEditing:
                additionalinfoFormMode !== VIEWABLE_FORM_MODES.EDITING,
            }}
            initialValue={initialValues['custom:renovationsPerYear']}
            isEditing={additionalinfoFormMode === VIEWABLE_FORM_MODES.EDITING}
          >
            <option value="" />
            {getNumberOfNewBuildsOptions()}
          </Field>
        </div>
      </div>
      <div className={classes.formRow}>
        <div className={classNames(classes.formGroup, classes.halfFormGroup)}>
          <Field
            name="custom:employeesCount"
            component={TextField}
            props={{
              id: 'employeesCount-text-field',
              label: 'Number of Permanent Employee',
              fullWidth: true,
              type: 'number',
              inputProps: {
                maxLength: 240,
              },
            }}
            initialValue={initialValues['custom:employeesCount']}
            isEditing={additionalinfoFormMode === VIEWABLE_FORM_MODES.EDITING}
            validate={[positiveNumber]}
          />
        </div>
        <div className={classNames(classes.formGroup, classes.halfFormGroup)}>
          <Field
            name="custom:businessYears"
            component={TextField}
            props={{
              id: 'businessYears-text-field',
              label: 'Number of years your business has been open?',
              fullWidth: true,
              type: 'number',
              inputProps: {
                maxLength: 240,
              },
            }}
            initialValue={initialValues['custom:businessYears']}
            isEditing={additionalinfoFormMode === VIEWABLE_FORM_MODES.EDITING}
            validate={[positiveNumber]}
          />
        </div>
      </div>

      <div className={classes.actionsRow}>{renderFormButton()}</div>
    </form>
  );
};

AdditionalInformationsForm.propTypes = propTypes;

const mapStateToProps = createStructuredSelector({
  isUpdatingAdditionalInfo: selectIsUpdatingAdditionalInfo(),
  additionalinfoFormMode: selectAdditionalInfoFormMode(),
});

const mapDispatchToProps = dispatch => ({
  onEditUserProfile: profileType => dispatch(editUserProfile(profileType)),
  onEditUserProfileCancelled: profileType =>
    dispatch(editUserProfileCancelled(profileType)),
  onResetForm: form => dispatch(reset(form)),
  onUpdateUserProfile: (
    accessToken,
    profileType,
    userAttributes,
    businessAttributes,
    businessNumber,
    previousBusinessAttributes,
  ) =>
    dispatch(
      updateUserProfile(
        accessToken,
        profileType,
        userAttributes,
        businessAttributes,
        businessNumber,
        previousBusinessAttributes,
      ),
    ),
});

export default reduxForm({
  form: FORMS.BUILDER_PROFILE_ADDITIONAL,
  touchOnChange: true,
})(
  withStyles(styles)(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(AdditionalInformationsForm),
  ),
);
